import { Component, ViewChild, ComponentRef, EmbeddedViewRef, HostListener, OnInit, ElementRef, OnDestroy, Optional, Inject } from '@angular/core';
import { TemplatePortal, BasePortalOutlet, CdkPortalOutlet, ComponentPortal } from '@angular/cdk/portal';
import { ESCAPE } from '@angular/cdk/keycodes';
import { FocusTrapFactory, FocusTrap } from '@angular/cdk/a11y';
import { DOCUMENT } from '@angular/common';
import { ApDialogRef } from '../../services';

@Component({
    selector: 'ap-dialog-container',
    templateUrl: './dialog-container.component.html'
})
export class ApDialogContainer extends BasePortalOutlet implements OnInit, OnDestroy {

  private _focusTrap: FocusTrap;
  private _dialogRef: ApDialogRef<any>;
  /** Element that was focused before the dialog was opened. Save this to restore upon close. */
  private _elementFocusedBeforeDialogWasOpened: HTMLElement | null = null;

  /** The portal outlet inside of this container into which the dialog content will be loaded. */
  @ViewChild(CdkPortalOutlet, { static: true })
  _portalOutlet: CdkPortalOutlet;


  constructor(
    private focusTrapFactory: FocusTrapFactory,
    private _elementRef: ElementRef,
    @Optional() @Inject(DOCUMENT) private _document: any) {
    super();
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (event.keyCode === ESCAPE && this._dialogRef) {
      this.dismiss(0);
    }
  }

  ngOnInit(): void {
    //NOTE: Focus trap doesn't work when there are no focusable element on the dialog
    this._focusTrap = this.focusTrapFactory.create(this._elementRef.nativeElement);
    this._focusTrap.focusInitialElementWhenReady();

    //NOTE: This block was taken from dialog-container.ts in https://github.com/angular/components
    if (this._document) {
      this._elementFocusedBeforeDialogWasOpened = this._document.activeElement as HTMLElement;

      // Note that there is no focus method when rendering on the server.
      if (this._elementRef.nativeElement.focus) {
        // Move focus onto the dialog immediately in order to prevent the user from accidentally
        // opening multiple dialogs at the same time. Needs to be async, because the element
        // may not be focusable immediately.
        Promise.resolve().then(() => this._elementRef.nativeElement.focus());
      }
    }
  }

  ngOnDestroy(): void {
    this._focusTrap.destroy();
  }

  setDialogRef<T>(ref: ApDialogRef<T>) {
    this._dialogRef = ref;
  }

  attachComponentPortal<T>(portal: ComponentPortal<T>): ComponentRef<T> {
    return this._portalOutlet.attachComponentPortal(portal);
  }
  attachTemplatePortal<C>(portal: TemplatePortal<C>): EmbeddedViewRef<C> {
    return this._portalOutlet.attachTemplatePortal(portal);
  }

  dismiss(direction: number) {
    this._dialogRef.close();

    // TODO: (AT) Cannot do the below, because some dialogs are triggerred by a source element having focus.
    // If the source element is focused on close, then you would get stuck in a loop.

    if (this._elementFocusedBeforeDialogWasOpened && direction !== 0) {
      const sibling: any = direction > 0 ? this._elementFocusedBeforeDialogWasOpened.nextElementSibling : this._elementFocusedBeforeDialogWasOpened.previousElementSibling;
      if (sibling && sibling.focus) {
          sibling.focus();
      }
    }
  }
}
